import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Modal,
  FormControl,
  TextField,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CustomAlert from "../Common/Components/CustomAlert";
import CreateCompany from "./Modal/CreateCompany";
import Company from "./Tabs/Company";
import ShareholderList from "./Tabs/ShareholderList";

import { Typography, Stack } from "@mui/material";

import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import "./css/CompanyView.css";
import { FormatDateTime } from "../../utils/formateDate";

function TabPanel({ children, value, index, ...other }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      sx={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        display: value === index ? "block" : "none",
      }}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 600, md: 800 },
  maxWidth: "95vw",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  boxSizing: "border-box",
};

function CompanyView() {
  const { t } = useTranslation(["Company", "Common"]);
  const userState = useSelector((state) => state.user);

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(0);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurant, setRestaurant] = useState("");
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [currentNoEmplacement, setCurrentNoEmplacement] = useState(null);
  const [shareHoldersByEmplacement, setShareHoldersByEmplacement] = useState(
    [],
  );
  const [isAdmin, setIsAdmin] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [typeMessage, setTypeMessage] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const roles = userState.roles || [];
    setIsAdmin(
      roles.some((role) => role === "CompanyAdmin" || role === "AdminTI"),
    );
  }, [userState.roles]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleChange = (_, newValue) => setValue(newValue);

  const showError = (errorMessage) => {
    setTypeMessage("error");
    setMessage(errorMessage);
    setShowAlert(true);
  };

  const GetEmplacement = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Localization/GetAllRestaurants`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to retrieve locations.");
      }

      const data = await response.json();

      const locationWithoutCompany = {
        noEmplacement: -1,
        nomEmplacement: "Sans Emplacement",
        paysEn: null,
        paysFr: null,
        provinceEn: null,
        provinceFr: null,
        regionEn: null,
        regionFr: null,
      };

      setRestaurants([
        locationWithoutCompany,
        ...(Array.isArray(data) ? data : []),
      ]);
    } catch (error) {
      console.error("Error retrieving locations:", error);
      setRestaurants([]);
      showError("Impossible de récupérer les emplacements.");
    }
  }, [userState.token]);

  const GetCompanies = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/GetCompanies`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to retrieve companies.");
      }

      const data = await response.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error retrieving companies:", error);
      setCompanies([]);
      showError("Impossible de récupérer les entreprises.");
    }
  }, [userState.token]);

  const GetCompaniesWithoutEmplacement = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/GetCompaniesWithoutEmplacement`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to retrieve companies without location.");
      }

      const data = await response.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error retrieving companies without location:", error);
      setCompanies([]);
      showError("Impossible de récupérer les entreprises sans emplacement.");
    }
  }, [userState.token]);

  const GetCompagniesByEmplacement = useCallback(async () => {
    if (currentNoEmplacement == null) {
      setCompanies([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/GetCompaniesByEmplacement/${currentNoEmplacement}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error retrieving companies by location:", error);
      setCompanies([]);
      showError("Impossible de récupérer les entreprises de cet emplacement.");
    }
  }, [currentNoEmplacement, userState.token]);

  const GetShareHoldersByEmplacement = useCallback(
    async (noCompagnie = company?.noCompagnie) => {
      if (noCompagnie == null) {
        setShareHoldersByEmplacement([]);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/employee/GetShareHoldersByEmplacement/${noCompagnie}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to retrieve shareholders.");
        }

        const data = await response.json();
        setShareHoldersByEmplacement(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error retrieving shareholders:", error);
        setShareHoldersByEmplacement([]);
      }
    },
    [company?.noCompagnie, userState.token],
  );

  useEffect(() => {
    GetEmplacement();
    GetCompanies();
  }, [GetEmplacement, GetCompanies]);

  useEffect(() => {
    if (currentNoEmplacement != null) {
      GetCompagniesByEmplacement();
    }
  }, [currentNoEmplacement, GetCompagniesByEmplacement]);

  useEffect(() => {
    if (company?.noCompagnie != null) {
      GetShareHoldersByEmplacement(company.noCompagnie);
    } else {
      setShareHoldersByEmplacement([]);
    }
  }, [company?.noCompagnie, GetShareHoldersByEmplacement]);

  const handleChangeLocation = (_, newValue) => {
    setRestaurant(newValue || "");
    setCompany(null);
    setShareHoldersByEmplacement([]);

    if (!newValue) {
      setCurrentNoEmplacement(null);
      GetCompanies();
      return;
    }

    if (newValue === "Sans Emplacement") {
      setCurrentNoEmplacement(null);
      GetCompaniesWithoutEmplacement();
      return;
    }

    const selectedRestaurant = restaurants.find(
      (item) => item.nomEmplacement === newValue,
    );

    if (selectedRestaurant) {
      setCurrentNoEmplacement(selectedRestaurant.noEmplacement);
    } else {
      setCurrentNoEmplacement(null);
      GetCompanies();
    }
  };

  const handleChangeCompany = (_, newValue) => {
    setShareHoldersByEmplacement([]);

    if (!newValue) {
      setCompany(null);
      return;
    }

    const selectedCompany = companies.find(
      (item) => item.noCompagnie === newValue.noCompagnie,
    );

    if (!selectedCompany) return;

    setCompany({ ...selectedCompany });

    if (
      !selectedCompany.nomEmplacement ||
      selectedCompany.noEmplacement == null
    ) {
      setRestaurant("Sans Emplacement");
      setCurrentNoEmplacement(null);
    } else {
      setRestaurant(selectedCompany.nomEmplacement);
      setCurrentNoEmplacement(selectedCompany.noEmplacement);
    }
  };

  const handleCompanyUpdate = async () => {
    if (company?.noCompagnie == null) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/GetCompanyById/${company.noCompagnie}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to retrieve updated company.");
      }

      const updatedData = await response.json();
      const updatedCompany = Array.isArray(updatedData)
        ? updatedData[0] || null
        : updatedData;

      setCompany(updatedCompany ? { ...updatedCompany } : null);

      if (currentNoEmplacement != null) {
        await GetCompagniesByEmplacement();
      } else if (restaurant === "Sans Emplacement") {
        await GetCompaniesWithoutEmplacement();
      } else {
        await GetCompanies();
      }

      if (updatedCompany?.noCompagnie != null) {
        await GetShareHoldersByEmplacement(updatedCompany.noCompagnie);
      }
    } catch (error) {
      console.error("Error refreshing company:", error);
      showError("Impossible d'actualiser les informations de l'entreprise.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: "#f5f7fb",
        p: { xs: 1.5, sm: 3, md: 4 },
        boxSizing: "border-box",
      }}
    >
      {/* Main container */}
      <Box sx={{ maxWidth: 1600, mx: "auto", width: "100%" }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 3.5 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 54,
                height: 54,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#e8efff",
                color: "#2855b7",
                borderRadius: 2.5,
              }}
            >
              <BusinessRoundedIcon sx={{ fontSize: 30 }} />
            </Box>

            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: "1.4rem", md: "1.8rem" },
                  fontWeight: 750,
                  color: "#17243b",
                  letterSpacing: "-0.6px",
                  lineHeight: 1.3,
                }}
              >
                {t("Company")}
              </Typography>

              <Typography variant="body2" sx={{ color: "#718096", mt: 0.5 }}>
                {t("Company")} · {t("Location")}
              </Typography>
            </Box>
          </Stack>

          <Button
            onClick={handleOpen}
            variant="contained"
            startIcon={<AddRoundedIcon />}
            sx={{
              minHeight: 44,
              px: 2.5,
              borderRadius: 2,
              bgcolor: "#2855b7",
              boxShadow: "0 4px 12px rgba(40,85,183,0.18)",
              textTransform: "none",
              fontSize: "0.9rem",
              fontWeight: 650,
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: "#1d4295",
                boxShadow: "0 6px 16px rgba(40,85,183,0.24)",
              },
            }}
          >
            {t("NewCompany")}
          </Button>
        </Stack>

        {/* Create company modal */}
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="create-company-title"
        >
          <Box sx={modalStyle} className="CompanyManagement__Modal">
            <CreateCompany />
          </Box>
        </Modal>

        {/* Filters card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            border: "1px solid #e6eaf0",
            borderRadius: 3,
            bgcolor: "#ffffff",
            boxShadow: "0 2px 8px rgba(20,35,60,0.025)",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.2}
            sx={{ mb: 2.5 }}
          >
            <FilterAltRoundedIcon sx={{ color: "#526b94", fontSize: 22 }} />

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "#26364f" }}
            >
              {t("Filters", { defaultValue: "Filtres" })}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
              alignItems: "start",
            }}
          >
            {/* Location */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: "#526078",
                }}
              >
                {t("Location")}
              </Typography>

              <Autocomplete
                id="autocomplete-emplacement"
                options={restaurants.map((item) => item.nomEmplacement)}
                value={restaurant || null}
                onChange={handleChangeLocation}
                popupIcon={<KeyboardArrowDownRoundedIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("Location")}
                    size="small"
                  />
                )}
                noOptionsText="Aucun emplacement disponible"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#ffffff",
                    minHeight: 44,
                  },
                }}
              />
            </Box>

            {/* Company */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: "#526078",
                }}
              >
                {t("Company")}
              </Typography>

              <Autocomplete
                id="autocomplete-company-name"
                value={company}
                onChange={handleChangeCompany}
                options={companies}
                getOptionLabel={(option) =>
                  option
                    ? `${option.nom ?? ""} - ${FormatDateTime(
                        option.dateOuverture,
                      )}`
                    : ""
                }
                isOptionEqualToValue={(option, selected) =>
                  option?.noCompagnie === selected?.noCompagnie
                }
                disabled={!companies.length}
                popupIcon={<KeyboardArrowDownRoundedIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("Company")}
                    size="small"
                  />
                )}
                noOptionsText="Aucune société disponible"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: companies.length ? "#ffffff" : "#f6f7f9",
                    minHeight: 44,
                  },
                }}
              />
            </Box>

            {/* Store number */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: "#526078",
                }}
              >
                {t("StoreNumber")}
              </Typography>

              <Autocomplete
                id="autocomplete-store-number"
                value={company}
                onChange={handleChangeCompany}
                options={companies}
                getOptionLabel={(option) =>
                  option?.noAccpac != null ? String(option.noAccpac) : ""
                }
                isOptionEqualToValue={(option, selected) =>
                  option?.noCompagnie === selected?.noCompagnie
                }
                disabled={!companies.length}
                popupIcon={<KeyboardArrowDownRoundedIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("StoreNumber")}
                    size="small"
                  />
                )}
                noOptionsText="Aucun numéro de magasin disponible"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: companies.length ? "#ffffff" : "#f6f7f9",
                    minHeight: 44,
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Tabs and content */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e6eaf0",
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#ffffff",
            boxShadow: "0 3px 12px rgba(20,35,60,0.035)",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              px: { xs: 1, sm: 2.5 },
              bgcolor: "#ffffff",
              borderBottom: "1px solid #e8edf4",
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="Company management tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 58,
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  bgcolor: "#2855b7",
                },
                "& .MuiTab-root": {
                  minHeight: 58,
                  minWidth: { xs: 130, sm: 170 },
                  px: 2.5,
                  textTransform: "none",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#78869b",
                  gap: 1,
                  "&.Mui-selected": {
                    color: "#2855b7",
                    fontWeight: 750,
                  },
                },
              }}
            >
              <Tab
                icon={<ApartmentRoundedIcon />}
                iconPosition="start"
                label={t("Company")}
                id="company-tab-0"
                aria-controls="company-tabpanel-0"
              />

              <Tab
                icon={<GroupsRoundedIcon />}
                iconPosition="start"
                label={t("Shareholders")}
                id="company-tab-1"
                aria-controls="company-tabpanel-1"
              />
            </Tabs>
          </Box>

          <Box
            sx={{
              p: { xs: 1.5, sm: 3, md: 3.5 },
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <TabPanel value={value} index={0}>
              <Company
                isAdmin={isAdmin}
                company={company}
                emplacements={restaurants}
                shareHoldersByEmplacement={shareHoldersByEmplacement}
                onCompanyUpdate={handleCompanyUpdate}
              />
            </TabPanel>

            <TabPanel value={value} index={1}>
              <ShareholderList
                isAdmin={isAdmin}
                company={company}
                shareHoldersByEmplacement={shareHoldersByEmplacement}
                refreshShareholders={GetShareHoldersByEmplacement}
              />
            </TabPanel>
          </Box>
        </Paper>

        {/* Notifications */}
        <CustomAlert
          open={showAlert}
          closeMessage={() => setShowAlert(false)}
          typeMessage={typeMessage}
          message={message}
        />
      </Box>
    </Box>
  );
}

export default CompanyView;
